import { useState, useEffect } from "react";
import { useAuth } from "@/lib/AuthContext";
import API from "@/lib/api";
import { MessageSquare, Calendar, Pill, ChevronRight, Sparkles, BookOpen, MessageCircle, LayoutDashboard } from "lucide-react";
import QuickActionCard from "@/components/QuickActionCard";
import AppointmentCard from "@/components/AppointmentCard";
import DailyCheckInCard from "@/components/DailyCheckInCard";
import MedicationCard from "@/components/MedicationCard";
import ADHDAssistantCard from "@/components/ADHDAssistantCard";
import ThemeToggleButton from "@/components/ThemeToggleButton";
import PhonePromptBanner from "@/components/PhonePromptBanner";
import GetStartedChecklist from "@/components/GetStartedChecklist";
import { Link } from "react-router-dom";

const quotes = [
  "Your brain isn't broken - it's running on a different OS.",
  "Progress, not perfection. One tab at a time.",
  "Your ideas aren't scattered - they're just enthusiastically distributed.",
  "The right support changes everything. You're in the right place.",
  "Being neurodivergent means your brain works overtime - make sure the rest of you gets care too.",
  "You didn't forget to be amazing today.",
  "Managing your mental health is the bravest thing you do. Seriously.",
  "Some days you conquer the world. Some days you find the charger. Both count.",
  "Your nervous system is not your enemy - it just needs a little extra love.",
  "You started the app. You showed up. That matters more than you know.",
];

export default function Home() {
  const { user } = useAuth();
  const isProvider = user?.role === "admin";
  const [appointments, setAppointments] = useState([]);
  const [medications, setMedications] = useState([]);
  const [unreadConvos, setUnreadConvos] = useState(0);
  const [actionableRefills, setActionableRefills] = useState(0);
  const [checklistVisible, setChecklistVisible] = useState(false);
  const [forumUnread, setForumUnread] = useState(0);
  const [quote] = useState(() => quotes[Math.floor(Math.random() * quotes.length)]);

  useEffect(() => {
    if (!user) return;
    API.get("/appointments").then(({ data }) => {
      setAppointments((data || []).filter(a => a.status === "scheduled").slice(0, 3));
    }).catch(() => {});
    if (!isProvider) {
      API.get("/medications").then(({ data }) => setMedications(data || [])).catch(() => {});
      API.get("/refill-requests").then(({ data }) => {
        setActionableRefills((data || []).filter(r => r.status === "approved").length);
      }).catch(() => {});
    }
    API.get("/conversations").then(({ data }) => {
      setUnreadConvos((data || []).filter(c => c.unread_count > 0).length);
    }).catch(() => {});
    const fetchForumUnread = () => API.get("/notifications/unread").then(({ data }) => {
      setForumUnread((data || []).filter(n => n.type === "forum_reply").length);
    }).catch(() => {});
    fetchForumUnread();
    const handler = () => fetchForumUnread();
    window.addEventListener("notifications:refresh", handler);
    return () => window.removeEventListener("notifications:refresh", handler);
  }, [user, isProvider]);

  const firstName = user?.first_name || user?.full_name?.split(" ")[0] || "there";
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="px-4 py-6 max-w-lg mx-auto space-y-6 animate-fade-in" data-testid="patient-dashboard">
      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-2">
          <p className="text-sm text-muted-foreground">{getGreeting()}</p>
          <div className="flex items-center gap-2">
            <ThemeToggleButton />
            <time
              dateTime={new Date().toISOString().slice(0, 10)}
              className="text-[11px] font-medium tracking-wider uppercase text-muted-foreground/80 shrink-0"
              data-testid="home-today-date"
            >
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </time>
          </div>
        </div>
        <h2 className="text-2xl font-display font-semibold text-foreground tracking-tight">{firstName}</h2>
        <p className="text-sm text-muted-foreground flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5 text-secondary shrink-0" />
          <span>{quote}</span>
        </p>
      </div>

      <DailyCheckInCard />

      <GetStartedChecklist onVisibility={setChecklistVisible} />
      {!checklistVisible && <PhonePromptBanner />}

      <div className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Quick Actions</h3>
        <div className={`grid gap-2 ${isProvider ? "grid-cols-2" : "grid-cols-3"}`} data-testid="quick-actions">
          <QuickActionCard to="/messages" icon={MessageSquare} label="Messages" badge={unreadConvos > 0 ? `${unreadConvos}` : null} />
          <QuickActionCard to={isProvider ? "/provider" : "/appointments"} icon={isProvider ? LayoutDashboard : Calendar} label={isProvider ? "Patients" : "Appointments"} />
          {!isProvider && (
            <QuickActionCard to="/medications" icon={Pill} label="Meds" badge={actionableRefills > 0 ? `${actionableRefills}` : null} />
          )}
        </div>
      </div>

      {!isProvider && medications.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Today's Medication</h3>
          <MedicationCard medication={medications[0]} />
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Resources</h3>
          <Link to="/resources" className="text-xs text-primary font-medium flex items-center gap-0.5 hover:underline">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <Link to="/resources" className="block bg-card border border-border rounded-xl p-4 hover:shadow-lg transition-shadow" data-testid="resources-card">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
              <BookOpen className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h4 className="font-medium text-sm">Evidence-Based Resources</h4>
              <p className="text-xs text-muted-foreground mt-1">Books, supplements, therapy guides & more</p>
            </div>
          </div>
        </Link>
        <Link
          to="/forum"
          className="block bg-gradient-to-br from-primary/10 via-secondary/10 to-background border border-primary/20 rounded-xl p-4 hover:border-primary/40 hover:shadow-md transition-all group"
          data-testid="home-forum-card"
        >
          <div className="flex items-start gap-3">
            <div className="relative shrink-0">
              <div className="w-10 h-10 rounded-lg bg-primary text-primary-foreground flex items-center justify-center group-hover:scale-105 transition-transform">
                <MessageCircle className="w-5 h-5" />
              </div>
              {forumUnread > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 ring-2 ring-background animate-pulse"
                  aria-label={`${forumUnread} new forum replies`}
                  data-testid="home-forum-dot"
                />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium text-sm">ADHD Club Forum</h4>
                {forumUnread > 0 && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-red-500 text-white px-1.5 py-0.5 rounded" data-testid="home-forum-pill">
                    {forumUnread} new
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Connect with fellow Elevated Psychiatry patients</p>
            </div>
          </div>
        </Link>
        <ADHDAssistantCard inline />
      </div>

      {appointments.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Upcoming</h3>
            <Link to="/appointments" className="text-xs text-primary font-medium flex items-center gap-0.5 hover:underline">
              View all <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {appointments.map((apt) => <AppointmentCard key={apt.id} appointment={apt} compact />)}
          </div>
        </div>
      )}

      <div className="bg-primary rounded-xl p-5 text-primary-foreground space-y-2" data-testid="practice-info">
        <h3 className="font-display text-lg font-semibold">Elevated Psychiatry</h3>
        <div className="flex gap-8">
          <div>
            <p className="text-sm font-semibold mb-1">Let's Talk (Call or Text)</p>
            <div className="space-y-0.5 text-sm opacity-80">
              <p>CA Phone: (619) 613-2791</p>
              <p>HI Phone: (808) 563-4128</p>
              <p>Fax: (619) 415-8415</p>
            </div>
          </div>
          <div className="ml-auto">
            <p className="text-sm font-semibold mb-1">Office Hours</p>
            <div className="space-y-0.5 text-sm opacity-80">
              <p>Monday - Friday</p>
              <p>8:00 AM - 5:00 PM</p>
            </div>
          </div>
        </div>
        <p className="text-xs opacity-70 italic border-t border-white/20 pt-3">
          Please note: All sessions are virtual unless otherwise discussed
        </p>
      </div>
    </div>
  );
}
